import {
  useMutation,
  useQuery,
  useQueryClient,
  UseMutationResult,
  UseQueryResult,
} from "@tanstack/react-query";
import { axios } from "@via/api/axios.tsx";
import { type AxiosResponse } from "axios";
import { useNotifications } from "@via/hooks/useNotifications.tsx";
import { getErrorResponseMessage } from "@via/utils/helpers.tsx";
import {
  readPendingTermination,
  readUnconfirmedTermination,
  type UnconfirmedTermination,
} from "@via/pages/projects/runs/terminate-refusal";
import {
  ErrorNotification,
  LogsInterface,
  SelectOption,
  type ParameterInterface,
} from "@via/utils/interfaces.tsx";
import {
  RunStatus,
  type GetRunAppStatusResponseBody,
  type GetRunAttemptsResponse,
  type GetRunAttemptsForManagementResponse,
  type GetRunDetailsResponseBody,
  type GetRunPipelineScriptsResponseBody,
  type GetRunStatusResponseBody,
  type InitiateRunRequestBody,
  type NewRunAttemptRequestBody,
  type RenameRunAttemptRequestBody,
  type RunAttemptManagementRequestBody,
  type RunAttemptManagementResponse,
  type RunAttemptTileDbRequestBody,
  type RunEstimation,
  type RunAnalyticsEstimation,
  RunAnalyticsFilterMode,
  type GetRunAnalyticsEstimationsRequestQuery,
  type RunInputFileResponseBody,
  type RunPipelineInput,
  type RunReportListResponse,
  type SaveRunRequestBody,
  type GetUploadedFileRequestBody,
  type PremiumRunSupportRequestBody,
  type RunParameterListResponseBody,
  type DuplicateRunRequestBody,
  type DuplicateRunResponseBody,
  type MoveRunRequestParams,
  type MoveRunRequestBody,
  type ShareRunResponseBody,
  type ShareRunRequestBody,
  type GenerateRunReportDownloadUrlsResponseBody,
  type RunReportDownloadFile,
  type GetRunDuplicationStatusResponseBody,
  type GetDuplicateCheckResponseBody,
  type GetNextProjectPipelineIdResponseBody,
  DuplicationStatus,
  type VmetaDataSetFileResponse,
  GenerateRunReportDownloadUrlsRequestBody,
  type CreateStandaloneProcessRunAttemptRequestBody,
  type CreateStandaloneProcessRunAttemptResponseBody,
  parseRunExecutionRefusal,
  type RunExecutionRefusalBody,
} from "common";
import { useNavigate } from "react-router-dom";
import type { LaunchRef } from "@via/pages/projects/runs/use-launch-refusal";

export const fetchParameterList = async (): Promise<ParameterInterface[]> => {
  try {
    const res = await axios.get("/api/parameter/v1");
    return res.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const useParameterList = (): any => {
  return useQuery({
    queryKey: ["params", "/api/parameter/v1"],
    queryFn: fetchParameterList,
    retry: false,
    staleTime: 60 * 60 * 1000,
  });
};

export const fetchRunPipelineDetails = async (
  id: string
): Promise<Record<string, any>> => {
  try {
    const res = await axios.get(`/api/run/v1/${id}/pipeline-details`);
    return res.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const useRunPipelineDetails = (id: string): any => {
  return useQuery({
    queryKey: [`/api/run/v1/${id}/pipeline-details`],
    queryFn: async () => await fetchRunPipelineDetails(id),
    retry: false,
  });
};

export const fetchRunPipelineInputs = async (
  id: string
): Promise<RunPipelineInput[]> => {
  try {
    const res = await axios.get(`/api/run/v1/pipeline/${id}/inputs`);
    return res.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const useRunPipelineInputs = (id: string): any => {
  return useQuery({
    queryKey: [`/api/run/v1/pipeline/${id}/inputs`],
    queryFn: async () => await fetchRunPipelineInputs(id),
    retry: false,
  });
};

export const fetchVmetaDatasetFiles = async (
  datasetId: string
): Promise<VmetaDataSetFileResponse> => {
  try {
    const res = await axios.post(
      `/api/v1/vmeta/dataset/${datasetId}/files/search`,
      { take: 1000, sort: "name", order: "asc" }
    );
    return res.data;
  } catch (error) {
    console.error(error);
    return { data: [], meta: { field_types: [] } };
  }
};
export const fetchInputFileList = async (
  id: string
): Promise<RunInputFileResponseBody[]> => {
  try {
    const res = await axios.get(`/api/run/v1/${id}/input-files`);
    return res.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const useInputFileList = (id: string): any => {
  return useQuery({
    queryKey: [`/api/run/v1/${id}/input-files`],
    queryFn: async () => await fetchInputFileList(id),
    retry: false,
  });
};

export const isDatasetExistingWithFiles = async (
  datasetName: string
): Promise<boolean> => {
  try {
    const baseUrl = "/api/collection/v1";
    const queryParams = new URLSearchParams({
      take: "100",
      filter: `name:eq=${datasetName}`,
    });
    const url = `${baseUrl}?${queryParams.toString()}`;
    const res = await axios.get(url);

    return res.data?.data.some(({ fileCount }) => fileCount > 0);
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const useCreateRunAttempt = (): any => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { setNotification, NOTIFICATION } = useNotifications();
  return useMutation({
    mutationFn: async (payload: NewRunAttemptRequestBody) => {
      const res = await axios.post("/api/run/v1/new-run-attempt", payload);
      return res.data;
    },
    onSuccess: (response, payload: NewRunAttemptRequestBody): void => {
      setNotification({
        id: "new-run-attempt-successfully-created",
        message: "New Run Successfully Created",
        variant: NOTIFICATION.SUCCESS,
      });
      // Invalidate and refetch the project run list
      queryClient.invalidateQueries({
        queryKey: [`/api/project/v1/${payload.projectId}/runs`],
      });
      queryClient.invalidateQueries({ queryKey: ["runList"] });
      queryClient.invalidateQueries({
        queryKey: ["pipelineList"],
      });
      navigate(`/runs/${response.projectPipelineId}`);
    },
    onError: error => {
      setNotification({
        id: "new-run-attempt-failed",
        message: "New Run Attempt Failed",
        details: "New Run could not be created",
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};

/**
 * Creates an unlaunched run attempt for a single process, posting to
 * POST /api/v1/process/{processId}/run-attempt. Mirrors useCreateRunAttempt above: same
 * navigation on success, same runs list invalidation, so a run created this way shows up in
 * the project's run list without a manual refresh.
 *
 * The response is { projectPipelineId }, not { runId }: matches the sibling
 * /api/run/v1/new-run-attempt endpoint, which already returns that value under that name.
 */
export const useCreateStandaloneProcessRunAttempt = (
  processId: number
): UseMutationResult<
  CreateStandaloneProcessRunAttemptResponseBody,
  ErrorNotification,
  CreateStandaloneProcessRunAttemptRequestBody
> => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { setNotification, NOTIFICATION } = useNotifications();
  return useMutation<
    CreateStandaloneProcessRunAttemptResponseBody,
    ErrorNotification,
    CreateStandaloneProcessRunAttemptRequestBody
  >({
    mutationFn: async (
      payload: CreateStandaloneProcessRunAttemptRequestBody
    ): Promise<CreateStandaloneProcessRunAttemptResponseBody> => {
      const res = await axios.post(
        `/api/v1/process/${processId}/run-attempt`,
        payload
      );
      return res.data;
    },
    onSuccess: (
      response: CreateStandaloneProcessRunAttemptResponseBody,
      payload: CreateStandaloneProcessRunAttemptRequestBody
    ): void => {
      setNotification({
        id: "standalone-process-run-attempt-successfully-created",
        message: "New Run Successfully Created",
        variant: NOTIFICATION.SUCCESS,
      });
      // Invalidate and refetch the project's run list, the same two keys
      // useCreateRunAttempt invalidates, so the new run appears without a manual refresh.
      queryClient.invalidateQueries({
        queryKey: [`/api/project/v1/${payload.projectId}/runs`],
      });
      queryClient.invalidateQueries({ queryKey: ["runList"] });
      navigate(`/runs/${response.projectPipelineId}`);
    },
    onError: error => {
      setNotification({
        id: "standalone-process-run-attempt-failed",
        message: "New Run Attempt Failed",
        // A fixture bound launch is refused with a message written for a person to act on
        // (an unknown port, a moved or changed fixture file), so show it rather than a generic line.
        details:
          error?.response?.data?.message ?? "New Run could not be created",
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};

export const fetchRunDetails = async (
  id: string,
  attemptId?: SelectOption["value"]
): Promise<GetRunDetailsResponseBody> => {
  try {
    const res = await axios.get(`/api/run/v1/${id}/details`, {
      params: attemptId ? { attemptId } : {},
    });
    return res.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const useRunDetails = (
  id: string,
  attemptId?: SelectOption["value"]
): any => {
  return useQuery({
    queryKey: [
      "runDetails",
      `/api/run/v1/${id}/details`,
      `/api/run/v1/${id}/details?attemptId=${attemptId}`,
    ],
    queryFn: async () => await fetchRunDetails(id, attemptId),
    retry: false,
  });
};

export const fetchRunPipelineScripts = async (
  id: string
): Promise<GetRunPipelineScriptsResponseBody> => {
  try {
    const res = await axios.get(`/api/run/v1/${id}/pipeline-scripts`);
    return res.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const useRunPipelineScripts = (id: string): any => {
  return useQuery({
    queryKey: [`/api/run/v1/${id}/pipeline-scripts`],
    queryFn: async () => await fetchRunPipelineScripts(id),
    retry: false,
  });
};

export const fetchPipelineRunDetails = async (
  id: string
): Promise<RunParameterListResponseBody> => {
  try {
    const res = await axios.get(`/api/run/v1/${id}/parameter-list`);
    return res.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const usePipelineRunDetails = (id: string): any => {
  return useQuery({
    queryKey: [`/api/run/v1/${id}/parameter-list`],
    queryFn: async () => await fetchPipelineRunDetails(id),
    retry: false,
  });
};

export const useSaveRun = (): any => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();
  return useMutation({
    mutationFn: async ({
      id,
      payload,
    }: {
      id: string;
      payload: SaveRunRequestBody;
    }) => {
      const res = await axios.patch(`/api/run/v1/${id}/save`, payload);
      return res.data;
    },
    onSuccess: (): void => {
      setNotification({
        id: "run-details-successfully-saved",
        message: "Run Details Successfully Saved",
        variant: NOTIFICATION.SUCCESS,
      });
      queryClient.invalidateQueries({
        queryKey: ["runDetails"],
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "run-details-saving-failed",
        message: "Run Details Saving Failed",
        details:
          getErrorResponseMessage(error) ?? "Run Details could not be saved",
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};

export const fetchRunReportList = async (
  runId: string,
  triggerSync: boolean,
  attemptId?: SelectOption["value"]
): Promise<RunReportListResponse> => {
  try {
    const res = await axios.get(`/api/run/v1/${runId}/reports`, {
      params: {
        attemptId: attemptId ? attemptId : undefined,
        triggerSync: triggerSync ? "true" : undefined,
      },
    });
    return res.data as RunReportListResponse;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const useRunReportList = ({
  runId,
  attemptId,
  enabled = false,
  triggerSync = false,
}: {
  runId: string;
  attemptId?: SelectOption["value"];
  enabled?: boolean;
  triggerSync?: boolean;
}): any => {
  return useQuery({
    queryKey: [
      `/api/run/v1/${runId}/reports`,
      `/api/run/v1/${runId}/reports?attemptId=${attemptId}`,
      `/api/run/v1/${runId}/reports?triggerSync=${triggerSync}`,
    ],
    queryFn: async (): Promise<RunReportListResponse> =>
      await fetchRunReportList(runId, triggerSync, attemptId),
    retry: false,
    enabled,
  });
};

export const fetchRunStatus = async (
  runId: string,
  attemptId?: SelectOption["value"]
): Promise<GetRunStatusResponseBody> => {
  try {
    const res = await axios.get(`/api/run/v1/${runId}/status`, {
      params: attemptId ? { attemptId } : {},
    });
    return res.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const useRunStatus = (
  runId: string,
  attemptId?: SelectOption["value"]
): any => {
  // const queryClient = useQueryClient();
  return useQuery({
    queryKey: [
      "runStatus",
      `/api/run/v1/${runId}/status`,
      `/api/run/v1/${runId}/status?attemptId=${attemptId}`,
    ],
    queryFn: async (): Promise<GetRunStatusResponseBody> => {
      const response = await fetchRunStatus(runId, attemptId);
      // const shouldRefetch = response?.runStatus
      //   ? ![
      //       RunStatus.NextError,
      //       RunStatus.Error,
      //       RunStatus.NextSuccess,
      //       RunStatus.Terminated,
      //       RunStatus.NotSubmitted,
      //     ].includes(response?.runStatus)
      //   : true;
      // if (!shouldRefetch) {
      //   queryClient.invalidateQueries({
      //     queryKey: [`/api/run/v1/${runId}/logs`],
      //   });
      // }
      return response;
    },
    staleTime: 0,
    retry: false,
    refetchInterval: data => {
      // Determine if refetch should continue based on runStatus
      const shouldRefetch = data?.state?.data?.runStatus
        ? ![
            RunStatus.NextError,
            RunStatus.Error,
            RunStatus.NextSuccess,
            RunStatus.Terminated,
            RunStatus.NotSubmitted,
          ].includes(data?.state?.data?.runStatus)
        : true;
      // Return refetch interval if should refetch, otherwise disable refetching
      return shouldRefetch ? 20000 : false;
    },
    refetchIntervalInBackground: true, // Continue refetching even when the page is not focused
  });
};

export type InitiateRunVariables = InitiateRunRequestBody & {
  /** 3G-3a: the identity of the launch, handed back to onRefused. Never sent to the server. */
  launchRef?: LaunchRef;
};

export type UseInitiateRunOptions = {
  /**
   * Called FIRST on a 409 refusal, with the refusal and the launchRef the mutation was given (spec 5.1). The page
   * owns the current view and the generation counters, so it alone can judge whether the refusal is current; it
   * returns whether it accepted it. A rejected refusal is silent in every surface: no toast either.
   */
  onRefused?: (
    refusal: RunExecutionRefusalBody,
    launchRef: LaunchRef | undefined
  ) => boolean;
};

export const useInitiateRun = (options: UseInitiateRunOptions = {}): any => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();
  return useMutation({
    mutationFn: async ({
      launchRef: _launchRef,
      ...payload
    }: InitiateRunVariables) => {
      const res = await axios.post(`/api/run/v1/initiate-run`, payload);
      return res.data;
    },
    onSuccess: async (_, payload: InitiateRunVariables): Promise<void> => {
      setNotification({
        id: "run-successfully-initiated",
        message: "Run Successfully Initiated",
        variant: NOTIFICATION.SUCCESS,
      });
      // Invalidate and refetch the run details, attempts, status, logs and reports
      await queryClient.invalidateQueries({
        queryKey: ["runDetails"],
      });
      await queryClient.invalidateQueries({
        queryKey: ["runAttempts"],
      });
      // Remove rather than invalidate: the logs query key is identical for every
      // attempt, so invalidating keeps serving the PREVIOUS attempt's logs while
      // the refetch is in flight (data stays, isLoading stays false, so no loading
      // state renders). Dropping the entry makes the panel load the new attempt
      // from scratch instead of flashing the old one.
      queryClient.removeQueries({
        queryKey: [`/api/run/v1/${payload.runId}/logs`],
      });
      await queryClient.invalidateQueries({
        queryKey: [`/api/run/v1/${payload.runId}/reports`],
      });
      await queryClient.invalidateQueries({
        queryKey: ["runStatus"],
      });
      await queryClient.invalidateQueries({
        queryKey: ["runList"],
      });
    },
    onError: async (error, payload: InitiateRunVariables) => {
      // `response` is absent on a network error; every read below is optional so the handler itself never
      // throws while the mutation is already rejecting (spec section 6, "No response").
      const response = (error as any).response;
      const status: number | undefined = response?.status;
      // 389 is HttpStatusCode.VIA_RETRY, not a typo: run.tsx marks the environment updated and resubmits,
      // and this silence is what keeps that recovery silent (D7). Refusal parsing sits BESIDE it.
      if (status !== 389) {
        const refusal = parseRunExecutionRefusal(status, response?.data);
        if (refusal) {
          const accepted = options.onRefused
            ? options.onRefused(refusal, payload.launchRef)
            : true;
          if (accepted) {
            setNotification({
              id: "run-launch-refused",
              message: "Run Launch Refused",
              details: refusal.message,
              variant: NOTIFICATION.ERROR,
            });
          }
        } else {
          setNotification({
            id: "run-initiation-failed",
            message: "Run Initiation Failed",
            details: response?.data?.message ?? "Run could not be initiated",
            variant: NOTIFICATION.ERROR,
          });
        }
      }
      // Invalidate and refetch the run details, attempts, status, logs and reports
      await queryClient.invalidateQueries({
        queryKey: ["runDetails"],
      });
      await queryClient.invalidateQueries({
        queryKey: ["runAttempts"],
      });
      await queryClient.invalidateQueries({
        queryKey: [`/api/run/v1/${payload.runId}/logs`],
      });
      await queryClient.invalidateQueries({
        queryKey: [`/api/run/v1/${payload.runId}/reports`],
      });
      await queryClient.invalidateQueries({
        queryKey: ["runStatus"],
      });
      await queryClient.invalidateQueries({
        queryKey: ["runList"],
      });
      throw error;
    },
  });
};

export interface TerminateRunVariables {
  id: string;
  /** Agent runs only: mark the run Terminated although the agent cannot confirm its job stopped. */
  withoutConfirmation?: boolean;
}

export const useTerminateRun = (options?: {
  /** Called instead of the failure toast when the agent could not confirm the job stopped. */
  onUnconfirmed?: (refusal: UnconfirmedTermination) => void;
}): any => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();
  const timeoutIds: ReturnType<typeof setTimeout>[] = [];

  return useMutation({
    mutationFn: async ({ id, withoutConfirmation }: TerminateRunVariables) => {
      const res = await axios.get(`/api/run/v1/${id}/terminate`, {
        params: withoutConfirmation ? { withoutConfirmation: true } : {},
      });
      return res.data;
    },
    onMutate: ({ id }: TerminateRunVariables) => {
      // Immediately start refetching run logs 5 times at 10-second intervals,
      // without waiting for the terminate request to complete.
      // This ensures logs stay up to date as the run terminates.
      for (let i = 0; i < 5; i++) {
        const timeoutId = setTimeout(() => {
          queryClient.refetchQueries({ queryKey: [`/api/run/v1/${id}/logs`] });
        }, i * 10000);
        timeoutIds.push(timeoutId);
      }
    },
    onSettled: () => {
      // Clear any remaining timeouts in case the mutation finishes early or component unmounts
      timeoutIds.forEach(clearTimeout);
    },
    onSuccess: async (): Promise<void> => {
      setNotification({
        id: "run-terminated",
        message: "Run Terminated",
        details: "",
        variant: NOTIFICATION.SUCCESS,
      });
      await queryClient.invalidateQueries({
        queryKey: ["runStatus"],
      });
      await queryClient.invalidateQueries({
        queryKey: ["runList"],
      });
    },
    onError: async (error: ErrorNotification) => {
      const unconfirmed = readUnconfirmedTermination(error);
      if (unconfirmed && options?.onUnconfirmed) {
        options.onUnconfirmed(unconfirmed);
        return;
      }
      const pending = readPendingTermination(error);
      if (pending) {
        setNotification({
          id: "run-termination-pending",
          message: "Run Is Stopping",
          details: [pending.message, pending.remedy].filter(Boolean).join(" "),
          variant: NOTIFICATION.ALERT,
        });
        await queryClient.invalidateQueries({
          queryKey: ["runStatus"],
        });
        return;
      }
      setNotification({
        id: "run-termination-failed",
        message: "Run Termination Failed",
        details:
          getErrorResponseMessage(error) ??
          "There was an error during termination your run",
        variant: NOTIFICATION.ERROR,
      });
      await queryClient.invalidateQueries({
        queryKey: ["runStatus"],
      });
      throw error;
    },
  });
};

export const useDeleteRun = (): any => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation({
    mutationFn: async (id: string) => {
      const res = await axios.delete(`/api/run/v1/${id}`);
      return res.data;
    },
    onSuccess: (): void => {
      setNotification({
        id: "run-successfully-deleted",
        message: "Run Successfully Deleted",
        details: "This action cannot be undone",
        variant: NOTIFICATION.SUCCESS,
      });
      queryClient.invalidateQueries({
        queryKey: ["runList"],
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "run-deletion-failed",
        message: "Run Deletion Failed",
        details:
          getErrorResponseMessage(error) ??
          "There was an error deleting your run",
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};

export const fetchLatestRunLogs = async (
  id: string,
  attemptId?: SelectOption["value"]
): Promise<LogsInterface[]> => {
  try {
    const res = await axios.get(`/api/run/v1/${id}/logs`, {
      params: attemptId ? { attemptId } : {},
    });
    return res.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const useLatestRunLogs = (
  id: string,
  shouldRefetch: boolean,
  attemptId?: SelectOption["value"]
): any => {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: [
      `/api/run/v1/${id}/logs`,
      `/api/run/v1/${id}/logs?attemptId=${attemptId}`,
    ],
    queryFn: async (): Promise<LogsInterface[]> => {
      const response = await fetchLatestRunLogs(id, attemptId);
      queryClient.invalidateQueries({
        queryKey: ["runStatus"],
      });
      return response;
    },
    refetchInterval: shouldRefetch ? 10000 : false, // Refetch every 10 seconds if `shouldRefetch` is true
    refetchIntervalInBackground: true, // Continue refetching even when the page is not focused
    staleTime: 0,
    retry: false,
  });
};

export const useFetchRunReportTabularPreview = (
  routePath: string,
  byteLimit: number,
  options?: { enabled?: boolean }
): any => {
  return useQuery({
    queryKey: ["runReportPreview", routePath, byteLimit],
    queryFn: async () => {
      const response = await axios.get(routePath, {
        withCredentials: false,
        headers: { Range: `bytes=0-${byteLimit - 1}` },
        // Keep the body as a raw string; axios would otherwise try to coerce it.
        transformResponse: raw => raw,
      });
      const data =
        typeof response.data === "string"
          ? response.data
          : String(response.data);
      // A server that ignores the Range header returns 200 with the WHOLE
      // body instead of 206 with a partial one. Truncate defensively so a
      // 40MB+ string is never handed to the parser — that's the freeze this
      // hook exists to prevent.
      //
      // Measure the real UTF-8 byte length rather than `data.length` (JS
      // string length counts UTF-16 code units): for non-ASCII content
      // (e.g. 3-byte-per-char scripts) `.length` undercounts bytes by up
      // to 3x, so slicing on it could keep ~3x the intended budget. Encode
      // once and slice the byte array instead.
      if (response.status !== 206) {
        const bytes = new TextEncoder().encode(data);
        if (bytes.length > byteLimit) {
          // A multi-byte character may straddle the cut point; that final
          // (possibly partial) character decodes to U+FFFD, which is
          // harmless here — the caller drops the trailing partial line
          // anyway before parsing.
          return new TextDecoder().decode(bytes.slice(0, byteLimit));
        }
      }
      return data;
    },
    enabled: options?.enabled ?? !!routePath,
    retry: false,
  });
};

export const fetchRunAppStatus = async (
  runUUID: string
): Promise<GetRunAppStatusResponseBody> => {
  try {
    const res = await axios.get(`/api/run/v1/${runUUID}/app-status`);
    return res.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const useRunAppStatus = (runUUID: string): any => {
  return useQuery({
    queryKey: [`/api/run/v1/${runUUID}/app-status`],
    queryFn: async () => await fetchRunAppStatus(runUUID),
    retry: false,
  });
};

export const fetchRunAttempts = async (
  runId: string
): Promise<GetRunAttemptsResponse> => {
  try {
    const res = await axios.get(`/api/run/v1/${runId}/attempts`);
    return res.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const useRunAttempts = (runId: string): any => {
  return useQuery({
    queryKey: ["runAttempts", `/api/run/v1/${runId}/attempts`],
    queryFn: async () => await fetchRunAttempts(runId),
    retry: false,
  });
};

export const fetchRunAttemptsForManagement = async (
  runId: string
): Promise<GetRunAttemptsForManagementResponse> => {
  const res = await axios.get(`/api/run/v1/${runId}/attempts/management`);
  return res.data;
};

export const useRunAttemptsForManagement = (
  runId: string,
  enabled: boolean = true
): UseQueryResult<GetRunAttemptsForManagementResponse, Error> => {
  return useQuery({
    queryKey: [
      "runAttemptsManagement",
      `/api/run/v1/${runId}/attempts/management`,
    ],
    queryFn: async () => await fetchRunAttemptsForManagement(runId),
    retry: false,
    enabled: enabled && !!runId,
  });
};

export const useSoftDeleteRunAttempts = (): UseMutationResult<
  RunAttemptManagementResponse,
  ErrorNotification,
  { runId: string } & RunAttemptManagementRequestBody
> => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation({
    mutationFn: async ({
      runId,
      attemptIds,
    }: { runId: string } & RunAttemptManagementRequestBody) => {
      const res = await axios.patch(
        `/api/run/v1/${runId}/attempts/soft-delete`,
        { attemptIds }
      );
      return res.data;
    },
    onSuccess: () => {
      setNotification({
        id: "run-attempts-soft-deleted",
        message: "Moved to Trash",
        details:
          "The selected attempts have been moved to trash. You can restore them later.",
        variant: NOTIFICATION.SUCCESS,
      });
      queryClient.invalidateQueries({
        queryKey: ["runAttemptsManagement"],
      });
      queryClient.invalidateQueries({
        queryKey: ["runAttempts"],
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "run-attempts-soft-delete-failed",
        message: "Move to Trash Failed",
        details:
          error.response?.data?.message ??
          "Failed to move the selected attempts to trash",
        variant: NOTIFICATION.ERROR,
      });
    },
  });
};

export const useRestoreRunAttempts = (): UseMutationResult<
  RunAttemptManagementResponse,
  ErrorNotification,
  { runId: string } & RunAttemptManagementRequestBody
> => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation({
    mutationFn: async ({
      runId,
      attemptIds,
    }: { runId: string } & RunAttemptManagementRequestBody) => {
      const res = await axios.patch(`/api/run/v1/${runId}/attempts/restore`, {
        attemptIds,
      });
      return res.data;
    },
    onSuccess: () => {
      setNotification({
        id: "run-attempts-restored",
        message: "Attempts Restored",
        details: "The selected attempts have been restored",
        variant: NOTIFICATION.SUCCESS,
      });
      queryClient.invalidateQueries({
        queryKey: ["runAttemptsManagement"],
      });
      queryClient.invalidateQueries({
        queryKey: ["runAttempts"],
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "run-attempts-restore-failed",
        message: "Restore Failed",
        details:
          error.response?.data?.message ??
          "Failed to restore the selected attempts",
        variant: NOTIFICATION.ERROR,
      });
    },
  });
};

export const usePurgeRunAttempts = (): UseMutationResult<
  RunAttemptManagementResponse,
  ErrorNotification,
  { runId: string } & RunAttemptManagementRequestBody
> => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation({
    mutationFn: async ({
      runId,
      attemptIds,
    }: { runId: string } & RunAttemptManagementRequestBody) => {
      const res = await axios.delete(`/api/run/v1/${runId}/attempts/purge`, {
        data: { attemptIds },
      });
      return res.data;
    },
    onSuccess: () => {
      setNotification({
        id: "run-attempts-purged",
        message: "Attempts Permanently Deleted",
        details: "The selected attempts have been permanently removed",
        variant: NOTIFICATION.SUCCESS,
      });
      queryClient.invalidateQueries({
        queryKey: ["runAttemptsManagement"],
      });
      queryClient.invalidateQueries({
        queryKey: ["runAttempts"],
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "run-attempts-purge-failed",
        message: "Permanent Deletion Failed",
        details:
          error.response?.data?.message ??
          "Failed to permanently delete the selected attempts",
        variant: NOTIFICATION.ERROR,
      });
    },
  });
};

export const useRenameRunAttempt = (): UseMutationResult<
  RunAttemptManagementResponse,
  ErrorNotification,
  { runId: string } & RenameRunAttemptRequestBody
> => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation({
    mutationFn: async ({
      runId,
      attemptId,
      name,
    }: { runId: string } & RenameRunAttemptRequestBody) => {
      const res = await axios.patch(`/api/run/v1/${runId}/attempts/rename`, {
        attemptId,
        name,
      });
      return res.data;
    },
    onSuccess: () => {
      setNotification({
        id: "run-attempt-renamed",
        message: "Attempt Renamed",
        details: "The attempt has been renamed successfully",
        variant: NOTIFICATION.SUCCESS,
      });
      queryClient.invalidateQueries({
        queryKey: ["runAttemptsManagement"],
      });
      queryClient.invalidateQueries({
        queryKey: ["runAttempts"],
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "run-attempt-rename-failed",
        message: "Rename Failed",
        details:
          error.response?.data?.message ?? "Failed to rename the attempt",
        variant: NOTIFICATION.ERROR,
      });
    },
  });
};

export const useRemoveRunAttemptTileDb = (): UseMutationResult<
  RunAttemptManagementResponse,
  ErrorNotification,
  { runId: string } & RunAttemptTileDbRequestBody
> => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation({
    mutationFn: async ({
      runId,
      attemptId,
    }: { runId: string } & RunAttemptTileDbRequestBody) => {
      const res = await axios.delete(`/api/run/v1/${runId}/attempts/tiledb`, {
        data: { attemptId },
      });
      return res.data;
    },
    onSuccess: () => {
      setNotification({
        id: "run-attempt-tiledb-removed",
        message: "Removed From Sample Tracker",
        details:
          "The current Sample Tracker snapshot has been removed for this run.",
        variant: NOTIFICATION.SUCCESS,
      });
      queryClient.invalidateQueries({
        queryKey: ["runAttemptsManagement"],
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "run-attempt-tiledb-remove-failed",
        message: "Sample Tracker Removal Failed",
        details:
          error.response?.data?.message ??
          "Failed to remove the current Sample Tracker snapshot",
        variant: NOTIFICATION.ERROR,
      });
    },
  });
};

export const useQueueRunAttemptTileDbIngestion = (): UseMutationResult<
  RunAttemptManagementResponse,
  ErrorNotification,
  { runId: string } & RunAttemptTileDbRequestBody
> => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation({
    mutationFn: async ({
      runId,
      attemptId,
    }: { runId: string } & RunAttemptTileDbRequestBody) => {
      const res = await axios.post(
        `/api/run/v1/${runId}/attempts/tiledb/reingest`,
        { attemptId }
      );
      return res.data;
    },
    onSuccess: () => {
      setNotification({
        id: "run-attempt-tiledb-queued",
        message: "Sample Tracker Rebuild Queued",
        details:
          "The latest successful attempt has been queued to rebuild Sample Tracker.",
        variant: NOTIFICATION.SUCCESS,
      });
      queryClient.invalidateQueries({
        queryKey: ["runAttemptsManagement"],
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "run-attempt-tiledb-queue-failed",
        message: "Queue Sample Tracker Rebuild Failed",
        details:
          error.response?.data?.message ??
          "Failed to queue a Sample Tracker rebuild for the selected attempt",
        variant: NOTIFICATION.ERROR,
      });
    },
  });
};

export const useGetUploadedFile = (runId: string): any => {
  return useMutation({
    mutationFn: async (payload: GetUploadedFileRequestBody) => {
      const res = await axios.post(
        `/api/run/v1/${runId}/uploaded-file`,
        payload
      );
      return res.data;
    },
  });
};

export const fetchRunReportDirectories = async (
  runId: string
): Promise<string[]> => {
  try {
    const res = await axios.get(`/api/run/v1/${runId}/reports-dirs`);
    return res.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const useRunReportDirectories = (runId: string): any => {
  return useQuery({
    queryKey: [`/api/run/v1/${runId}/reports-dirs`],
    queryFn: async () => await fetchRunReportDirectories(runId),
    retry: false,
  });
};

export const useUploadReport = (runId: string, runUUID: string): any => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation({
    mutationFn: async (payload: { file: File; reportDirectory: string }) => {
      const formData = new FormData();
      formData.append("file", payload.file);
      formData.append("dir", payload.reportDirectory);

      const res = await axios.post(
        `/api/run/v1/${runId}/reports/upload/${runUUID}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      return res.data;
    },
    onSuccess: (): void => {
      setNotification({
        id: "run-report-file-uploaded-successfully",
        message: "Report File Uploaded",
        details: "Report File is successfully uploaded",
        variant: NOTIFICATION.SUCCESS,
      });
      // Invalidate and refetch the reports
      queryClient.invalidateQueries({
        queryKey: [`/api/run/v1/${runId}/reports`],
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "run-report-file-upload-failed",
        message: "Uploading Report File Failed",
        details:
          getErrorResponseMessage(error) ?? "Report File could not be saved",
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};

export const usePremiumRunSupport = (): any => {
  const { setNotification, NOTIFICATION } = useNotifications();
  return useMutation({
    mutationFn: async (payload: PremiumRunSupportRequestBody) => {
      const res = await axios.post(
        "/api/contact/v1/premium-run-support",
        payload
      );
      return res.data;
    },
    onSuccess: (response): void => {
      setNotification({
        id: "premium-run-support-request-sent",
        message: "Premium Run Support Request Sent Successfully",
        details: response.message,
        variant: NOTIFICATION.SUCCESS,
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "premium-run-support-request-failed",
        message: "Premium Run Support Request Failed",
        details:
          getErrorResponseMessage(error) ??
          "Premium Run Support Request could not be sent",
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};

export const useDuplicateRun = (): UseMutationResult<
  DuplicateRunResponseBody,
  ErrorNotification,
  DuplicateRunRequestBody & { runId: string }
> => {
  const { setNotification, NOTIFICATION } = useNotifications();
  const queryClient = useQueryClient();
  return useMutation<
    DuplicateRunResponseBody,
    ErrorNotification,
    DuplicateRunRequestBody & { runId: string }
  >({
    mutationFn: async (
      payload: DuplicateRunRequestBody & { runId: string }
    ): Promise<DuplicateRunResponseBody> => {
      const { runId, ...body } = payload;
      const res = await axios.post(`/api/run/v1/${runId}/duplicate`, body);
      return res.data;
    },
    onSuccess: (_, variables): void => {
      setNotification({
        id: "duplicate-run-success",
        message: "Run Duplication Started",
        details:
          "The duplication process has started. Please wait for it to complete.",
        variant: NOTIFICATION.SUCCESS,
      });
      queryClient.removeQueries({
        queryKey: [`/api/run/v1/${variables.runId}/duplication-status`],
      });

      queryClient.invalidateQueries({
        queryKey: [`/api/run/v1/${variables.runId}/duplication-status`],
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "duplicate-run-failed",
        message: "Run Duplication Failed",
        details:
          getErrorResponseMessage(error) ?? "Run could not be duplicated",
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};

export const useChangeProject = (): any => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();
  return useMutation({
    mutationFn: async (payload: MoveRunRequestParams & MoveRunRequestBody) => {
      const res = await axios.post(`/api/run/v1/${payload.runId}/move`, {
        projectId: payload.projectId,
      });
      return res.data;
    },
    onSuccess: (response): void => {
      setNotification({
        id: "move-run-success",
        message: "Move Run Successfully",
        details: response.message,
        variant: NOTIFICATION.SUCCESS,
      });
      queryClient.invalidateQueries({
        queryKey: ["runDetails"],
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "move-run-failed",
        message: "Move Run Failed",
        details: getErrorResponseMessage(error) ?? "Run could not be moved",
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};

export const useShareRun = ({
  runId,
}: {
  runId: string;
}): UseMutationResult<
  ShareRunResponseBody,
  ErrorNotification,
  ShareRunRequestBody
> => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation<
    ShareRunResponseBody,
    ErrorNotification,
    ShareRunRequestBody
  >({
    mutationFn: async (
      payload: ShareRunRequestBody
    ): Promise<ShareRunResponseBody> => {
      const res = await axios.post(`/api/v1/run/${runId}/share`, payload);
      return res.data;
    },
    onSuccess: (data: ShareRunResponseBody): void => {
      setNotification({
        id: "run-shared-successfully",
        message: "Run Shared Successfully",
        details: data?.message,
        variant: NOTIFICATION.SUCCESS,
      });
      // Invalidate and refetch the run details
      queryClient.invalidateQueries({
        queryKey: ["runDetails"],
      });
    },
    onError: (error: ErrorNotification) => {
      const errorMessage =
        error.response?.data?.error ||
        error.response?.data?.message ||
        "An error occurred while sharing the run";
      setNotification({
        id: "run-share-failed",
        message: "Run Share Failed",
        details: errorMessage,
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};

/**
 * Generate Download URLs
 */
export const fetchGenerateRunReportDownloadUrls = async (
  runId: string,
  files: RunReportDownloadFile[],
  downloadMode?: GenerateRunReportDownloadUrlsRequestBody["downloadMode"]
): Promise<GenerateRunReportDownloadUrlsResponseBody> => {
  try {
    const res = await axios.post(
      `/api/run/v1/${runId}/generate-download-urls`,
      { files, downloadMode }
    );
    return res.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const useGenerateRunReportDownloadUrls = (): UseMutationResult<
  GenerateRunReportDownloadUrlsResponseBody,
  ErrorNotification,
  GenerateRunReportDownloadUrlsRequestBody & { runId: string }
> => {
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation<
    GenerateRunReportDownloadUrlsResponseBody,
    ErrorNotification,
    GenerateRunReportDownloadUrlsRequestBody & { runId: string }
  >({
    mutationFn: async ({
      runId,
      files,
      downloadMode,
    }: GenerateRunReportDownloadUrlsRequestBody & {
      runId: string;
    }): Promise<GenerateRunReportDownloadUrlsResponseBody> =>
      await fetchGenerateRunReportDownloadUrls(runId, files, downloadMode),
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "generate-download-urls-failed",
        message: "Failed to generate download URLs",
        details:
          error?.response?.data?.message ||
          "Failed to generate download links. Please try again.",
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};

export const useDownloadRunParams = (): UseMutationResult<
  AxiosResponse<Blob>,
  ErrorNotification,
  {
    runId: string;
    selectedRunAttempt?: SelectOption;
    runAttempts?: SelectOption[];
  }
> => {
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation<
    AxiosResponse<Blob>,
    ErrorNotification,
    {
      runId: string;
      selectedRunAttempt?: SelectOption;
      runAttempts?: SelectOption[];
    }
  >({
    mutationFn: async ({ runId, selectedRunAttempt, runAttempts }) => {
      const attemptIndexInList = runAttempts?.findIndex(
        item => item.value === selectedRunAttempt?.value
      );
      let attemptNumber = 0;
      if (attemptIndexInList && runAttempts) {
        attemptNumber = runAttempts?.length - attemptIndexInList;
      } else if (runAttempts && runAttempts?.length > 0) {
        attemptNumber = runAttempts?.length - 1;
      }
      const attemptId = selectedRunAttempt?.value;
      return await axios.get(`/api/run/v1/${runId}/parameters/download`, {
        params: attemptId
          ? { attemptId, attemptIndex: attemptNumber }
          : { attemptIndex: attemptNumber },
        responseType: "blob",
        withCredentials: true,
      });
    },
    onSuccess: () => {
      setNotification({
        id: "run-parameters-download-success",
        message: "Run parameter download started",
        details: "",
        variant: NOTIFICATION.SUCCESS,
      });
    },
    onError: error => {
      setNotification({
        id: "run-parameters-download-failed",
        message: "Download failed",
        details:
          error?.response?.data?.message ??
          "An error occurred, please try again or contact support.",
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};

export const useDownloadRunConfig = (): UseMutationResult<
  AxiosResponse<Blob>,
  ErrorNotification,
  {
    runId: string;
    selectedRunAttempt?: SelectOption;
    runAttempts?: SelectOption[];
  }
> => {
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation<
    AxiosResponse<Blob>,
    ErrorNotification,
    {
      runId: string;
      selectedRunAttempt?: SelectOption;
      runAttempts?: SelectOption[];
    }
  >({
    mutationFn: async ({ runId, selectedRunAttempt, runAttempts }) => {
      const attemptIndexInList = runAttempts?.findIndex(
        item => item.value === selectedRunAttempt?.value
      );
      let attemptNumber = 0;
      if (attemptIndexInList && runAttempts) {
        attemptNumber = runAttempts?.length - attemptIndexInList;
      } else if (runAttempts && runAttempts?.length > 0) {
        attemptNumber = runAttempts?.length - 1;
      }
      const attemptId = selectedRunAttempt?.value;
      return await axios.get(`/api/run/v1/${runId}/config/download`, {
        params: attemptId
          ? { attemptId, attemptIndex: attemptNumber }
          : { attemptIndex: attemptNumber },
        responseType: "blob",
        withCredentials: true,
      });
    },
    onSuccess: () => {
      setNotification({
        id: "run-config-download-success",
        message: "Run config download started",
        details: "",
        variant: NOTIFICATION.SUCCESS,
      });
    },
    onError: error => {
      setNotification({
        id: "run-config-download-failed",
        message: "Download failed",
        details:
          error?.response?.data?.message ??
          "An error occurred, please try again or contact support.",
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};

export const useDownloadRunLogs = (): UseMutationResult<
  AxiosResponse<Blob>,
  ErrorNotification,
  {
    runId: string;
    selectedRunAttempt?: SelectOption;
    runAttempts?: SelectOption[];
  }
> => {
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation<
    AxiosResponse<Blob>,
    ErrorNotification,
    {
      runId: string;
      selectedRunAttempt?: SelectOption;
      runAttempts?: SelectOption[];
    }
  >({
    mutationFn: async ({ runId, selectedRunAttempt, runAttempts }) => {
      const attemptIndexInList = runAttempts?.findIndex(
        item => item.value === selectedRunAttempt?.value
      );
      let attemptNumber = 0;
      if (attemptIndexInList && runAttempts) {
        attemptNumber = runAttempts?.length - attemptIndexInList;
      } else if (runAttempts && runAttempts?.length > 0) {
        attemptNumber = runAttempts?.length - 1;
      }
      const attemptId = selectedRunAttempt?.value;
      return await axios.get(`/api/run/v1/${runId}/logs/download`, {
        params: attemptId
          ? { attemptId, attemptIndex: attemptNumber }
          : { attemptIndex: attemptNumber },
        responseType: "blob",
        withCredentials: true,
      });
    },
    onSuccess: () => {
      setNotification({
        id: "run-logs-download-success",
        message: "Run download started",
        details: "",
        variant: NOTIFICATION.SUCCESS,
      });
    },
    onError: error => {
      setNotification({
        id: "run-logs-download-failed",
        message: "Download failed",
        details:
          error?.response?.data?.message ??
          "No log files found to download. Please try again or contact support.",
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};

// Moved to common so shared code can use them; re-exported for existing importers.
export {
  fetchAvailableAIModels,
  useAvailableAIModels,
} from "@via/api/ai-models.api.tsx";

/**
 * Mock hook to check run duplication status
 * TODO: Replace with real API endpoint when backend is ready
 */
export const useGetDuplicateCheck = (
  runId: string,
  enabled: boolean = true
): UseQueryResult<GetDuplicateCheckResponseBody, Error> => {
  return useQuery({
    queryKey: [`/api/run/v1/${runId}/duplicate-check`],
    queryFn: async (): Promise<GetDuplicateCheckResponseBody> => {
      const res = await axios.get(`/api/run/v1/${runId}/duplicate-check`);
      return res.data;
    },
    enabled: enabled && !!runId,
    retry: false,
    staleTime: 60000, // Cache for 1 minute
  });
};

export const useGetRunDuplicationStatus = (
  runId: string,
  enabled: boolean = true
): UseQueryResult<GetRunDuplicationStatusResponseBody | null, Error> => {
  return useQuery({
    queryKey: [`/api/run/v1/${runId}/duplication-status`],
    queryFn: async () => {
      const res = await axios.get(`/api/run/v1/${runId}/duplication-status`);
      return res.data;
    },
    enabled: enabled && !!runId,
    retry: false,
    // Poll automatically when enabled and status is in progress
    refetchInterval: query => {
      if (!enabled || !runId) return false;
      const data = query.state.data;
      // Poll every 5 seconds if status is in progress
      return data?.status === DuplicationStatus.InProgress ? 5000 : false;
    },
    refetchIntervalInBackground: true,
  });
};

export const useCancelRunDuplication = (): UseMutationResult<
  { success: boolean; message: string },
  ErrorNotification,
  { runId: string }
> => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation({
    mutationFn: async (params: {
      runId: string;
    }): Promise<{ success: boolean; message: string }> => {
      const res = await axios.post(
        `/api/run/v1/${params.runId}/duplication-cancel`
      );
      return res.data;
    },
    onSuccess: (response, variables) => {
      setNotification({
        id: "cancel-duplication-success",
        message: "Duplication Cancelled",
        details: response.message,
        variant: NOTIFICATION.SUCCESS,
      });
      // Invalidate duplication status query to refetch
      queryClient.invalidateQueries({
        queryKey: [`/api/run/v1/${variables.runId}/duplication-status`],
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "cancel-duplication-failed",
        message: "Cancel Duplication Failed",
        details:
          error.response?.data?.message ?? "Failed to cancel duplication",
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};

export const useGetNextProjectPipelineId = (
  enabled: boolean = true
): UseQueryResult<GetNextProjectPipelineIdResponseBody, Error> => {
  return useQuery({
    queryKey: ["/api/v1/run/next-id"],
    queryFn: async () => {
      const res = await axios.get("/api/v1/run/next-id");
      return res.data;
    },
    enabled: enabled,
    retry: false,
    refetchOnMount: "always",
    staleTime: 0,
  });
};

export const fetchRunEstimationsByRunId = async (
  runId: string
): Promise<RunEstimation[]> => {
  try {
    const res = await axios.get(`/api/v1/run/${runId}/estimations`);
    return res.data as RunEstimation[];
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const useRunEstimationsByRunId = (
  runId: string,
  enabled: boolean = true
): UseQueryResult<RunEstimation[], Error> => {
  return useQuery<RunEstimation[], Error>({
    queryKey: [`/api/v1/run/${runId}/estimations`],
    queryFn: async () => await fetchRunEstimationsByRunId(runId),
    enabled: enabled && !!runId,
    retry: false,
  });
};

export const fetchRunAnalyticsEstimations = async (
  query: GetRunAnalyticsEstimationsRequestQuery
): Promise<RunAnalyticsEstimation[]> => {
  try {
    const res = await axios.get(`/api/v1/run/analytics/estimations`, {
      params: {
        days: query.days,
        filterMode: query.filterMode,
        pipelineGid: query.pipelineGid,
        pipelineIds: query.pipelineIds,
      },
    });
    return res.data as RunAnalyticsEstimation[];
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const useRunAnalyticsEstimations = (
  query: GetRunAnalyticsEstimationsRequestQuery,
  enabled: boolean = true
): UseQueryResult<RunAnalyticsEstimation[], Error> => {
  const filterMode = query.filterMode ?? RunAnalyticsFilterMode.AllRuns;
  const hasPipelineIds = !!query.pipelineIds?.trim();

  return useQuery<RunAnalyticsEstimation[], Error>({
    queryKey: [`/api/v1/run/analytics/estimations`, query],
    queryFn: async () => await fetchRunAnalyticsEstimations(query),
    enabled:
      enabled &&
      (filterMode === RunAnalyticsFilterMode.AllRuns ||
        (filterMode === RunAnalyticsFilterMode.PipelineName &&
          !!query.pipelineGid) ||
        (filterMode === RunAnalyticsFilterMode.PipelineVersion &&
          hasPipelineIds)),
    retry: false,
  });
};
